import { useTranslation } from 'react-i18next'
import { useState, useMemo } from 'react'
import * as Yup from 'yup'
import { FormikProvider, useFormik } from 'formik'

import { useDateChangeHandler } from '../../../hooks/useDateChangeHandler'
import { useBooleanSelectOption } from '../../../hooks/useBooleanSelectOption'
import { useCandidateRuleOption } from '../../../hooks/useCandidateRuleOption'
import { getBaseProgramValidation } from '../../../helpers/programValidation'

import { AcknowledgeModalState, EditProgramModalProps, FormValues } from './EditProgramModal.types'
import {
  GrantProgramEntryType,
  UpdateGrantProgramPayload
} from '@percent/admin-dashboard/api/actions/grantProgram/grantProgram.types'
import {
  SUPPRESSIBLE_EMAIL_NAMES,
  SuppressibleEmailName
} from '@percent/admin-dashboard/api/actions/grantProgram/suppressibleEmailNames'
import { useServices } from '@percent/admin-dashboard/containers/service/ServiceContext'
import { getChangedValues, useMutation, useQueryList } from '@percent/utility'
import {
  AcknowledgeModal,
  ActionModal,
  DateInput,
  Flex,
  FormField,
  MarkdownTextArea,
  Modal,
  MultiSelect,
  Select,
  TextInput
} from '@percent/lemonade'
import { useGrantsUtils } from '@percent/admin-dashboard/app/grants/utils/utils'

export function EditProgramModal({ open, setOpen, program, onUpdate }: Readonly<EditProgramModalProps>) {
  const [modalState, setModalState] = useState<AcknowledgeModalState | null>(null)
  const { grantProgramService } = useServices()
  const { t } = useTranslation()
  const { toIsoOrNull, toNumberOrNull, toNullIfEmpty } = useGrantsUtils()

  const [{ isPending }, { apiFunc: updateProgram }] = useMutation(
    grantProgramService.updateProgram,
    () => {
      setModalState('success')
      onUpdate?.()
    },
    () => setModalState('error')
  )

  const initialValues = useMemo<FormValues>(
    () => ({
      name: program?.name,
      description: program?.description,
      opensAt: program?.opensAt ? new Date(program.opensAt) : null,
      closesAt: program?.closesAt ? new Date(program.closesAt) : null,
      budgetMin: program?.budgetMin,
      budgetMax: program?.budgetMax,
      currency: program?.currency,
      nominationFormId: program?.nominationFormId,
      nominationDeadline: program?.nominationDeadline ? new Date(program.nominationDeadline) : null,
      nonprofitCcEmail: program?.nonprofitCcEmail || null,
      entryType: program?.entryType,
      candidateRule: program?.candidateRule,
      shareNominatorInfoWithOrg: program?.shareNominatorInfoWithOrg,
      emailSuppressionConfig: (program?.emailSuppressionConfig ?? []).filter((name): name is SuppressibleEmailName =>
        (SUPPRESSIBLE_EMAIL_NAMES as readonly string[]).includes(name)
      )
    }),
    [program]
  )

  // Preserve any suppression names the backend knows but the frontend allowlist does not
  // TODO: Remove once registry is returned in the metadata endpoint
  const unknownSuppressedNames = useMemo(
    () =>
      (program?.emailSuppressionConfig ?? []).filter(
        name => !(SUPPRESSIBLE_EMAIL_NAMES as readonly string[]).includes(name)
      ),
    [program?.emailSuppressionConfig]
  )

  const emailSuppressionOptions = useMemo(
    () => SUPPRESSIBLE_EMAIL_NAMES.map(name => ({ label: name, value: name })),
    []
  )

  const [{ data: programs }] = useQueryList(['/v1/admin/grants/programs'], grantProgramService.getAllPrograms, {})

  const nominationFormIds = useMemo(() => [...new Set(programs?.map(prog => prog.nominationFormId) ?? [])], [programs])

  const validationSchema = useMemo(
    () =>
      Yup.object().shape({
        name: Yup.string()
          .max(255, t('errorMessage.orgFieldCharacterLimit', { fieldName: 'Name' }))
          .nullable()
          .optional(),
        description: Yup.string().nullable().optional(),
        nominationFormId: Yup.string().optional(),
        ...getBaseProgramValidation(t)
      }),
    [t]
  )

  const normalizeValues = (values: FormValues) => ({
    name: toNullIfEmpty(values.name),
    description: toNullIfEmpty(values.description),
    opensAt: toIsoOrNull(values.opensAt),
    closesAt: toIsoOrNull(values.closesAt),
    nominationDeadline: toIsoOrNull(values.nominationDeadline),
    budgetMin: toNumberOrNull(values.budgetMin),
    budgetMax: toNumberOrNull(values.budgetMax),
    nonprofitCcEmail: toNullIfEmpty(values.nonprofitCcEmail),
    candidateRule: values.candidateRule,
    nominationFormId: values.nominationFormId,
    currency: values.currency,
    shareNominatorInfoWithOrg: values.shareNominatorInfoWithOrg,
    emailSuppressionConfig: [
      ...(values.emailSuppressionConfig ?? []),
      ...unknownSuppressedNames
    ] as SuppressibleEmailName[]
  })

  const formik = useFormik<FormValues>({
    enableReinitialize: true,
    validateOnMount: true,
    initialValues,
    validationSchema,
    onSubmit: async values => {
      const normalizedValues = normalizeValues(values)
      const normalizedInitialValues = normalizeValues(initialValues)

      const changedValues = getChangedValues(
        normalizedValues,
        normalizedInitialValues
      ) as Partial<UpdateGrantProgramPayload>

      const sortedSuppression = (config?: SuppressibleEmailName[]) => [...(config ?? [])].sort().join()

      if (
        sortedSuppression(normalizedValues.emailSuppressionConfig) ===
        sortedSuppression(normalizedInitialValues.emailSuppressionConfig)
      ) {
        delete changedValues.emailSuppressionConfig
      }

      if (Object.keys(changedValues).length === 0) {
        return
      }

      const payload: UpdateGrantProgramPayload = {
        ...changedValues
      }

      updateProgram({
        programId: program.id,
        payload
      })
    }
  })

  const { values, errors, touched, dirty, handleChange, handleBlur, handleSubmit, setFieldValue, resetForm, isValid } =
    formik

  const getFieldStatus = (fieldName: keyof FormValues) =>
    touched[fieldName] && errors[fieldName] ? 'danger' : 'default'

  const handleDateChange = useDateChangeHandler<FormValues>(setFieldValue)

  const shareNominatorInfoWithOrgOption = useBooleanSelectOption(values.shareNominatorInfoWithOrg)

  const { selectedOption: candidateRuleOption, options: candidateRuleOptions } = useCandidateRuleOption(
    values.candidateRule
  )

  const emailSuppressionDefaultValue = useMemo(
    () =>
      emailSuppressionOptions.filter(opt =>
        (values.emailSuppressionConfig ?? []).includes(opt.value as SuppressibleEmailName)
      ),
    [emailSuppressionOptions, values.emailSuppressionConfig]
  )

  const handleClose = () => {
    setOpen(false)
    setModalState(null)
    resetForm()
  }

  const successModal = modalState === 'success' && (
    <AcknowledgeModal
      result="positive"
      title={t('grants.programDetails.edit.success.title')}
      buttonText={t('button.done')}
      handleClose={handleClose}
      viewTestId="edit-program-success-modal"
      buttonTestId="done-edit-program-success-modal"
    />
  )

  const errorModal = modalState === 'error' && (
    <AcknowledgeModal
      result="negative"
      title={t('grants.programDetails.edit.error.title')}
      buttonText={t('button.close')}
      handleClose={handleClose}
      viewTestId="edit-program-error-modal"
      buttonTestId="close-edit-program-error-modal"
    />
  )

  return (
    <Modal open={open} onClose={() => setOpen(false)} showModalOverflow={false}>
      {successModal || errorModal || (
        <ActionModal
          title={t('grants.programDetails.edit.title')}
          loading={isPending}
          primaryButtonText={t('button.edit')}
          primaryBtnTestId="edit-program-modal-button"
          secondaryButtonText={t('button.cancel')}
          secondaryBtnTestId="cancel-edit-program-modal-button"
          handleSubmit={handleSubmit}
          handleClose={handleClose}
          disabled={!(isValid && dirty)}
        >
          <form onSubmit={handleSubmit}>
            <FormikProvider value={formik}>
              <Flex direction="column" gap={16}>
                <FormField
                  label={t('grants.program.modal.name')}
                  status={getFieldStatus('name')}
                  statusMessage={errors.name}
                  data-testid="name-field"
                >
                  <TextInput
                    name="name"
                    value={values.name}
                    placeholder={t('grants.program.modal.name.placeholder')}
                    onBlur={handleBlur}
                    onChange={handleChange}
                  />
                </FormField>
                <FormField
                  label={t('grants.program.modal.description')}
                  status={getFieldStatus('description')}
                  statusMessage={errors.description}
                  data-testid="description-field"
                >
                  <MarkdownTextArea
                    name="description"
                    content={values.description}
                    placeholder={t('grants.program.modal.description.placeholder')}
                    onChange={value => setFieldValue('description', value)}
                    testId="description-field"
                  />
                </FormField>
                <FormField
                  label={t('grants.program.modal.opensAt')}
                  status={getFieldStatus('opensAt')}
                  statusMessage={errors.opensAt}
                  data-testid="opensAt-field"
                >
                  <DateInput
                    name="opensAt"
                    value={values.opensAt}
                    placeholder={t('grants.program.modal.opensAt.placeholder')}
                    onChange={handleDateChange('opensAt')}
                    onBlur={handleBlur}
                    clearable
                    withinPopup
                    data-testid="opensAt-input"
                  />
                </FormField>
                <FormField
                  label={t('grants.program.modal.closesAt')}
                  status={getFieldStatus('closesAt')}
                  statusMessage={errors.closesAt}
                  data-testid="closesAt-field"
                >
                  <DateInput
                    name="closesAt"
                    value={values.closesAt}
                    placeholder={t('grants.program.modal.closesAt.placeholder')}
                    onChange={handleDateChange('closesAt')}
                    onBlur={handleBlur}
                    clearable
                    withinPopup
                    data-testid="closesAt-input"
                  />
                </FormField>
                <FormField
                  label={t('grants.program.modal.budgetMin')}
                  status={getFieldStatus('budgetMin')}
                  statusMessage={errors.budgetMin}
                  data-testid="budgetMin-field"
                >
                  <TextInput
                    name="budgetMin"
                    value={values.budgetMin?.toString() ?? ''}
                    placeholder={t('grants.program.modal.budgetMin.placeholder')}
                    onBlur={handleBlur}
                    onChange={handleChange}
                    data-testid="budgetMin-input"
                  />
                </FormField>
                <FormField
                  label={t('grants.program.modal.budgetMax')}
                  status={getFieldStatus('budgetMax')}
                  statusMessage={errors.budgetMax}
                  data-testid="budgetMax-field"
                >
                  <TextInput
                    name="budgetMax"
                    value={values.budgetMax?.toString() ?? ''}
                    placeholder={t('grants.program.modal.budgetMax.placeholder')}
                    onBlur={handleBlur}
                    onChange={handleChange}
                    data-testid="budgetMax-input"
                  />
                </FormField>
                <FormField
                  label={t('grants.program.modal.currency')}
                  status={getFieldStatus('currency')}
                  statusMessage={errors.currency}
                  description={t('grants.program.modal.currency.description')}
                  descriptionVariant="light"
                  data-testid="currency-field"
                >
                  <TextInput
                    name="currency"
                    value={values.currency ?? ''}
                    placeholder={t('grants.program.modal.currency.placeholder')}
                    onBlur={handleBlur}
                    onChange={handleChange}
                    data-testid="currency-input"
                  />
                </FormField>
                <FormField
                  label={t('grants.program.modal.entryFormId')}
                  status={getFieldStatus('nominationFormId')}
                  statusMessage={errors.nominationFormId}
                  data-testid="entryFormId-field"
                >
                  <Select
                    name="entryFormId"
                    searchable
                    placeholder={t('grants.program.modal.entryFormId.placeholder.updateProgram')}
                    options={nominationFormIds
                      .filter((id): id is string => Boolean(id))
                      .map(id => ({ label: id, value: id }))}
                    defaultValue={
                      values.nominationFormId
                        ? { label: values.nominationFormId, value: values.nominationFormId }
                        : undefined
                    }
                    onChange={event => setFieldValue('nominationFormId', event.value)}
                    data-testid="entryFormId-select"
                  />
                </FormField>
                <FormField
                  label={t('grants.program.modal.entryFormDeadline')}
                  status={getFieldStatus('nominationDeadline')}
                  statusMessage={errors.nominationDeadline}
                  data-testid="entryFormDeadline-field"
                >
                  <DateInput
                    name="nominationDeadline"
                    value={values.nominationDeadline}
                    placeholder={t('grants.program.modal.entryFormDeadline.placeholder')}
                    onChange={handleDateChange('nominationDeadline')}
                    onBlur={handleBlur}
                    clearable
                    withinPopup
                    data-testid="nominationDeadline-input"
                  />
                </FormField>
                <FormField
                  label={t('grants.program.modal.candidateRule')}
                  status={getFieldStatus('candidateRule')}
                  statusMessage={errors.candidateRule}
                  data-testid="candidateRule-field"
                >
                  <Select
                    name="candidateRule"
                    placeholder={t('grants.program.modal.candidateRule.placeholder')}
                    options={candidateRuleOptions}
                    defaultValue={candidateRuleOption}
                    onChange={event => setFieldValue('candidateRule', event.value)}
                    data-testid="candidateRule-select"
                  />
                </FormField>
                {values.entryType === GrantProgramEntryType.NOMINATION && (
                  <FormField
                    label={t('grants.program.modal.shareNominatorInfoWithOrg')}
                    status={getFieldStatus('shareNominatorInfoWithOrg')}
                    statusMessage={errors.shareNominatorInfoWithOrg}
                    data-testid="shareNominatorInfoWithOrg-field"
                  >
                    <Select
                      name="shareNominatorInfoWithOrg"
                      searchable
                      placeholder={t('grants.program.modal.shareNominatorInfoWithOrg.placeholder')}
                      options={[
                        { label: t('grants.program.modal.option.yes'), value: String(true) },
                        { label: t('grants.program.modal.option.no'), value: String(false) }
                      ]}
                      defaultValue={shareNominatorInfoWithOrgOption}
                      onChange={event => setFieldValue('shareNominatorInfoWithOrg', event.value === 'true')}
                      data-testid="shareNominatorInfoWithOrg-select"
                    />
                  </FormField>
                )}
                <FormField
                  label={t('grants.program.modal.nonprofitCcEmail')}
                  status={getFieldStatus('nonprofitCcEmail')}
                  statusMessage={errors.nonprofitCcEmail}
                  data-testid="nonprofitCcEmail-field"
                >
                  <TextInput
                    name="nonprofitCcEmail"
                    value={values.nonprofitCcEmail ?? ''}
                    placeholder={t('grants.program.modal.nonprofitCcEmail.placeholder')}
                    onBlur={handleBlur}
                    onChange={handleChange}
                    data-testid="nonprofitCcEmail-input"
                  />
                </FormField>
                <FormField
                  label={t('grants.program.modal.emailSuppressionConfig')}
                  data-testid="emailSuppressionConfig-field"
                >
                  <MultiSelect
                    key={program.id}
                    name="emailSuppressionConfig"
                    placeholder={t('grants.program.modal.emailSuppressionConfig.placeholder')}
                    options={emailSuppressionOptions}
                    defaultValue={emailSuppressionDefaultValue}
                    onChange={selected =>
                      setFieldValue(
                        'emailSuppressionConfig',
                        selected.map(o => o.value as SuppressibleEmailName)
                      )
                    }
                    allowClear
                    data-testid="emailSuppressionConfig-select"
                  />
                </FormField>
              </Flex>
            </FormikProvider>
          </form>
        </ActionModal>
      )}
    </Modal>
  )
}
